/**
 * Hierarchical navigation manager
 */

export class NavigationManager {
  constructor() {
    this.hierarchyData = null; // Full hierarchical data
    this.currentPath = []; // Current navigation path [root, p1, ...]
    this.onNavigate = null; // Callback when navigation changes
  }

  /**
   * Set the hierarchical data structure
   */
  setData(data) {
    console.log('🔄 NavigationManager.setData called');
    console.log('  Old path:', this.currentPath);
    console.log('  Old data title:', this.hierarchyData?.title);
    console.log('  New data title:', data?.title);

    this.hierarchyData = data;
    this.currentPath = ['root'];

    console.log('  New path (reset to root):', this.currentPath);
  }

  /**
   * Get the current node's full data (including text field)
   */
  getCurrentData() {
    if (!this.hierarchyData) return null;

    let current = this.hierarchyData;

    // Navigate to current level
    for (let i = 1; i < this.currentPath.length; i++) {
      const nodeId = this.currentPath[i];
      if (current.children && current.children[nodeId]) {
        current = current.children[nodeId];
      } else {
        // Invalid path, reset to root
        this.currentPath = ['root'];
        current = this.hierarchyData;
        break;
      }
    }

    return current;
  }

  /**
   * Get current graph data based on path
   */
  getCurrentGraph() {
    if (!this.hierarchyData) return null;

    let current = this.hierarchyData;

    // Navigate to current level
    for (let i = 1; i < this.currentPath.length; i++) {
      const nodeId = this.currentPath[i];
      if (current.children && current.children[nodeId]) {
        current = current.children[nodeId];
      } else {
        // Invalid path, reset to root
        this.currentPath = ['root'];
        current = this.hierarchyData;
        break;
      }
    }

    // Start with current level's nodes and edges
    const nodes = [...current.nodes];

    // CRITICAL: Normalize edges - backend sometimes returns source/target as objects instead of IDs
    const edges = current.edges.map(edge => ({
      ...edge,
      source: typeof edge.source === 'object' ? edge.source.id : edge.source,
      target: typeof edge.target === 'object' ? edge.target.id : edge.target,
    }));

    console.log('🎯 getCurrentGraph - starting with:', {
      nodeCount: nodes.length,
      edgeCount: edges.length,
      hasChildren: !!current.children,
      currentNodeIds: nodes.map(n => n.id),
      edgeRefs: edges.map(e => `${e.source}->${e.target}`)
    });

    // Add children's summary nodes with invisible connecting edges
    if (current.children && current.nodes.length > 0) {
      // Pick a central node to connect children to (first node or highest importance)
      const centralNode = current.nodes.reduce((prev, curr) =>
        (curr.importance || 0) > (prev.importance || 0) ? curr : prev
      );

      console.log('🎯 Adding summary nodes, central node:', centralNode.id);

      for (const [childId, childData] of Object.entries(current.children)) {
        if (childData.summary_node) {
          // Clone the summary node and mark it as having children
          const summaryNode = {
            ...childData.summary_node,
            hasChildren: true  // Mark for drill-down capability
          };
          nodes.push(summaryNode);

          // Add invisible edge to keep it close (stronger force)
          edges.push({
            source: centralNode.id,
            target: summaryNode.id,
            relationship: 'related_to',
            strength: 8,  // Higher strength to keep summary nodes close
            invisible: true  // Mark as invisible for rendering
          });

          console.log('  Added summary node:', summaryNode.id);
        }
      }
    }

    console.log('🎯 getCurrentGraph - final:', {
      nodeCount: nodes.length,
      edgeCount: edges.length,
      nodeIds: nodes.map(n => n.id)
    });

    // Validate that all edges reference existing nodes
    const nodeIds = new Set(nodes.map(n => n.id));
    const invalidEdges = edges.filter(e => !nodeIds.has(e.source) || !nodeIds.has(e.target));
    if (invalidEdges.length > 0) {
      console.error('❌ INVALID EDGES DETECTED (' + invalidEdges.length + '):', invalidEdges.map(e => ({
        source: e.source,
        target: e.target,
        sourceExists: nodeIds.has(e.source),
        targetExists: nodeIds.has(e.target)
      })));
      console.error('   Available node IDs:', Array.from(nodeIds));

      // CRITICAL: Filter out invalid edges to prevent rendering corruption
      const validEdges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
      console.warn('   Filtered edges from ' + edges.length + ' to ' + validEdges.length);

      return {
        nodes: nodes,
        edges: validEdges, // Return only valid edges
        metadata: current.metadata,
        title: current.title || 'Root',
        type: current.type || 'document'
      };
    }

    return {
      nodes: nodes,
      edges: edges,
      metadata: current.metadata,
      title: current.title || 'Root',
      type: current.type || 'document'
    };
  }

  /**
   * Drill down into a node's children
   */
  drillDown(nodeId) {
    const currentGraph = this.getCurrentGraph();
    const node = currentGraph.nodes.find(n => n.id === nodeId);

    if (!node || !node.hasChildren) {
      console.warn('Cannot drill down: node has no children');
      return false;
    }

    // Get the actual current level data
    let current = this.hierarchyData;
    for (let i = 1; i < this.currentPath.length; i++) {
      const pathId = this.currentPath[i];
      if (current.children && current.children[pathId]) {
        current = current.children[pathId];
      }
    }

    // Find which child this summary node belongs to
    let childKey = null;
    if (current.children) {
      for (const [key, childData] of Object.entries(current.children)) {
        if (childData.summary_node && childData.summary_node.id === nodeId) {
          childKey = key;
          break;
        }
      }
    }

    if (!childKey) {
      console.warn('Cannot find child for node:', nodeId);
      return false;
    }

    // Add child key to path (not the summary node ID)
    this.currentPath.push(childKey);

    if (this.onNavigate) {
      this.onNavigate(this.currentPath, this.getCurrentGraph());
    }

    return true;
  }

  /**
   * Navigate up to a specific level
   */
  navigateToLevel(level) {
    if (level < 0 || level >= this.currentPath.length) {
      return false;
    }

    // Truncate path to specified level
    this.currentPath = this.currentPath.slice(0, level + 1);

    if (this.onNavigate) {
      this.onNavigate(this.currentPath, this.getCurrentGraph());
    }

    return true;
  }

  /**
   * Navigate up one level
   */
  navigateUp() {
    if (this.currentPath.length <= 1) {
      return false; // Already at root
    }

    this.currentPath.pop();

    if (this.onNavigate) {
      this.onNavigate(this.currentPath, this.getCurrentGraph());
    }

    return true;
  }

  /**
   * Get breadcrumb data for UI
   */
  getBreadcrumbs() {
    const breadcrumbs = [];
    let current = this.hierarchyData;

    for (let i = 0; i < this.currentPath.length; i++) {
      const id = this.currentPath[i];

      breadcrumbs.push({
        id,
        level: i,
        title: current.title || 'Root',
        type: current.type || 'document',
        emoji: current.nodes?.[0]?.emoji || '📄'
      });

      // Navigate to next level for next iteration
      if (i < this.currentPath.length - 1) {
        const nextId = this.currentPath[i + 1];
        if (current.children && current.children[nextId]) {
          current = current.children[nextId];
        }
      }
    }

    return breadcrumbs;
  }

  /**
   * Check if we're at root level
   */
  isAtRoot() {
    return this.currentPath.length === 1;
  }

  /**
   * Get current depth
   */
  getCurrentDepth() {
    return this.currentPath.length - 1;
  }
}

/**
 * Render simplified breadcrumb (just current location)
 */
export function renderBreadcrumbs(breadcrumbs, onBreadcrumbClick) {
  const locationSpan = document.getElementById('current-location');
  if (!locationSpan) return;

  const currentCrumb = breadcrumbs[breadcrumbs.length - 1];
  if (currentCrumb) {
    locationSpan.textContent = `${currentCrumb.emoji} ${currentCrumb.title}`;
  }
}

/**
 * Build minimap tree structure recursively
 */
export function buildMinimapTree(hierarchyData, currentPath) {
  if (!hierarchyData) return '';

  const buildNode = (data, path, depth = 0) => {
    const nodeId = path[depth];
    const isCurrent = depth === currentPath.length - 1 && path.slice(0, depth + 1).join('/') === currentPath.join('/');
    const isParent = depth < currentPath.length - 1;

    const nodeClass = isCurrent ? 'current' : (isParent ? 'parent' : '');
    const emoji = data.nodes?.[0]?.emoji || '📄';
    const title = data.title || 'Root';

    let html = `<div class="minimap-node ${nodeClass}" data-path="${path.join(',')}" data-depth="${depth}">
      <span class="minimap-emoji">${emoji}</span>
      <span class="minimap-label">${title}</span>
    </div>`;

    // Render children if this node is in the current path or we're at a shallow depth
    if (data.children && (isParent || isCurrent || depth < 1)) {
      html += '<div class="minimap-children">';

      for (const [childId, childData] of Object.entries(data.children)) {
        const childPath = [...path.slice(0, depth + 1), childId];
        const isInCurrentPath = currentPath.length > depth + 1 && currentPath[depth + 1] === childId;

        if (isInCurrentPath || depth < 1) {
          html += buildNode(childData, childPath, depth + 1);
        } else {
          // Collapsed child node
          const childEmoji = childData.nodes?.[0]?.emoji || '📄';
          const childTitle = childData.title || childId;
          html += `<div class="minimap-node" data-path="${childPath.join(',')}" data-depth="${depth + 1}">
            <span class="minimap-emoji">${childEmoji}</span>
            <span class="minimap-label">${childTitle}</span>
          </div>`;
        }
      }

      html += '</div>';
    }

    return html;
  };

  return buildNode(hierarchyData, ['root'], 0);
}

/**
 * Render minimap
 */
export function renderMinimap(hierarchyData, currentPath, onNodeClick) {
  const container = document.getElementById('minimap-tree');
  if (!container) return;

  const tree = buildMinimapTree(hierarchyData, currentPath);
  container.innerHTML = tree;

  // Add click handlers
  container.querySelectorAll('.minimap-node').forEach(node => {
    node.addEventListener('click', (e) => {
      e.stopPropagation();
      const path = node.dataset.path.split(',');
      const depth = parseInt(node.dataset.depth);
      if (onNodeClick) {
        onNodeClick(depth);
      }
    });
  });
}

/**
 * Parse node ID to extract child indices
 * Examples:
 *   "root" => []
 *   "root_c0" => [0]
 *   "root_c0_c2" => [0, 2]
 */
export function parseNodeIdToIndices(nodeId) {
  if (nodeId === 'root') return [];

  const parts = nodeId.split('_');
  const indices = [];

  for (const part of parts) {
    if (part.startsWith('c')) {
      const index = parseInt(part.substring(1), 10);
      if (!isNaN(index)) {
        indices.push(index);
      }
    }
  }

  return indices;
}

/**
 * Find the line number in YAML text for a given navigation path
 * @param {string} yamlText - The YAML text content
 * @param {number[]} indices - Array of child indices [0, 2, 1]
 * @returns {number|null} - Line number (0-based) or null if not found
 */
export function findYAMLLineForPath(yamlText, indices) {
  const lines = yamlText.split('\n');

  // Root level - return first line
  if (indices.length === 0) {
    return 0;
  }

  // Track depth based on how many "children:" keywords we've encountered
  let depth = 0;
  // Track child count at each depth
  let childCountAtDepth = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip empty lines and comments
    if (!line.trim() || line.trim().startsWith('#')) {
      continue;
    }

    // Detect "children:" keyword - increases nesting depth
    if (line.includes('children:')) {
      depth++;
      childCountAtDepth[depth] = -1; // Will increment to 0 on first child
      continue;
    }

    // Detect list items (children)
    const match = line.match(/^(\s*)- text:/);
    if (match) {
      const indent = match[1].length;
      const indentLevel = Math.floor(indent / 2);

      // When we encounter a list item, we might be exiting nested levels
      // Determine actual depth based on indentation
      // Each "- text:" should be at depth 1, 2, 3, etc. based on indentation
      const actualDepth = indentLevel;

      // If we've come back to a shallower level, reset depth
      if (actualDepth < depth) {
        depth = actualDepth;
      }

      // Increment or initialize child count at this depth
      if (childCountAtDepth[depth] === undefined || childCountAtDepth[depth] === -1) {
        childCountAtDepth[depth] = 0;
      } else {
        childCountAtDepth[depth]++;
      }

      // Check if this matches our target path
      // We need to match all levels from depth 1 to indices.length
      let pathMatches = true;
      for (let j = 0; j < indices.length; j++) {
        const requiredDepth = j + 1;
        if (childCountAtDepth[requiredDepth] !== indices[j]) {
          pathMatches = false;
          break;
        }
      }

      // Also check we're at the right depth
      if (pathMatches && depth === indices.length) {
        return i;
      }
    }
  }

  return null; // Not found
}

/**
 * Highlight a specific line in a CodeMirror editor
 * @param {EditorView} editorView - CodeMirror editor instance
 * @param {number} lineNumber - Line number (0-based)
 */
export function highlightEditorLine(editorView, lineNumber) {
  if (!editorView || lineNumber === null) return;

  const doc = editorView.state.doc;
  const line = doc.line(lineNumber + 1); // CodeMirror uses 1-based line numbers

  if (!line) return;

  // Set selection to the line
  editorView.dispatch({
    selection: { anchor: line.from, head: line.to },
    scrollIntoView: true
  });
}

/**
 * Highlight the YAML text corresponding to the current navigation path
 * @param {EditorView} editorView - CodeMirror editor instance
 * @param {string[]} path - Navigation path like ['root', 'root_c0', 'root_c0_c2']
 */
export function highlightNavigationPath(editorView, path) {
  if (!editorView || !path || path.length === 0) return;

  const yamlText = editorView.state.doc.toString();
  if (!yamlText) return;

  // Get the current node ID (last element in path)
  const nodeId = path[path.length - 1];

  // Parse to get indices
  const indices = parseNodeIdToIndices(nodeId);

  // Find the line number
  const lineNumber = findYAMLLineForPath(yamlText, indices);

  if (lineNumber !== null) {
    highlightEditorLine(editorView, lineNumber);
  }
}
